import { useMemo, useState } from 'react';
import { Button, Checkbox, InlineNotification, Search } from '@carbon/react';
import { format0 } from 'utils/number';
import { useNearbyWells, type NearbySort } from './use-nearby-wells';
import { WellPickerTable } from './well-picker-table';
import type { PickerCandidate, SectionVertex } from './types';
import styles from './well-picker.module.scss';

/** How far either side of the line the sweep looks, until asked for more. */
const DEFAULT_RADIUS_FT = 5_280;

/** The distances the sweep offers, in feet. */
const RADII = [2_640, 5_280, 10_560, 26_400];

export interface WellPickerNearbyProps {
  /** The section line, which is what "nearby" is measured from. */
  vertices: SectionVertex[];
  /** Wells already on the section, so the sweep never offers one twice. */
  excludeUwis: string[];
  stagedUwis: string[];
  onStage: (well: PickerCandidate, checked: boolean) => void;
}

/**
 * The wells near the section line.
 *
 * The sweep is an explicit action rather than something that runs as the picker opens:
 * it is a spatial query against every well in the basin, and the user may have come
 * here knowing the name of the well they want, in which case the corridor never needed
 * searching. With no section drawn there is nothing to be near, and it says so instead
 * of searching.
 *
 * @param {WellPickerNearbyProps} props - Component props.
 * @return {JSX.Element} The pane.
 */
export const WellPickerNearby = ({
  vertices,
  excludeUwis,
  stagedUwis,
  onStage,
}: WellPickerNearbyProps): JSX.Element => {
  const [swept, setSwept] = useState(false);
  const [radiusFt, setRadiusFt] = useState(DEFAULT_RADIUS_FT);
  const [text, setText] = useState('');
  const [sort, setSort] = useState<NearbySort>('offset_ft');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  // Vertical holes only, matching what a Well to Well section accepts and how the map's
  // own picking is filtered. Widening is one click away, though: the sweep itself is not
  // gated, and a user checking what else is around should be able to see it.
  const [verticalOnly, setVerticalOnly] = useState(true);

  // Two points make a line, and "near the line" needs one.
  const hasSection = vertices.length >= 2;

  const request = useMemo(() => {
    if (!swept || !hasSection) {
      return null;
    }
    const trimmed = text.trim();
    return {
      vertices,
      radiusFt,
      text: trimmed === '' ? undefined : trimmed,
      excludeUwis,
      verticalOnly,
      // Total depth is resolved per page from survey geometry on the backend, so it
      // cannot be a server-side sort — the request stays nearest-first and the page
      // is re-ordered here. A useful side effect: sorting by depth never refetches.
      sortAttribute: sort === 'total_depth_ft' ? 'offset_ft' : sort,
      page,
      pageSize,
    };
  }, [
    swept,
    hasSection,
    vertices,
    radiusFt,
    text,
    excludeUwis,
    verticalOnly,
    sort,
    page,
    pageSize,
  ]);

  const { wells, totalCount, isLoading, error } = useNearbyWells(request);

  const candidates: PickerCandidate[] = useMemo(() => {
    const rows = wells.map((well) => ({
      uwi: well.uwi,
      name: well.name,
      operator: well.operator,
      totalDepthFt: well.totalDepthFt,
      spudDate: well.spudDate,
      offsetFt: well.offsetFt,
      // The sweep answers with attributes, not geometry: the cut places the well.
      lat: null,
      lon: null,
    }));
    if (sort !== 'total_depth_ft') {
      return rows;
    }
    // Ordered within the page — see the request above. Unknown depths sort last:
    // a well whose depth the survey cannot give is not the shallowest well.
    return [...rows].sort((a, b) => {
      if (a.totalDepthFt === null) {
        return b.totalDepthFt === null ? 0 : 1;
      }
      if (b.totalDepthFt === null) {
        return -1;
      }
      return a.totalDepthFt - b.totalDepthFt;
    });
  }, [wells, sort]);

  if (!hasSection) {
    return (
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="No section to search around"
        subtitle="Draw a section or pick two wells first — nearby is measured from the section line."
      />
    );
  }

  return (
    <>
      <div className={styles.sweep}>
        <div className={styles.radius}>
          <span className={styles.radiusLabel}>Within</span>
          {RADII.map((option) => (
            <Button
              key={option}
              kind={option === radiusFt ? 'primary' : 'tertiary'}
              size="sm"
              onClick={() => {
                setRadiusFt(option);
                setPage(1);
              }}
            >
              {`${format0(option)} ft`}
            </Button>
          ))}
        </div>
        <div className={styles.sweepActions}>
          <Checkbox
            id="cross-section-nearby-vertical-only"
            labelText="Vertical wells only"
            helperText="A section runs between vertical holes; others cannot be vertices."
            checked={verticalOnly}
            onChange={(_event, { checked }) => {
              setVerticalOnly(checked);
              setPage(1);
            }}
          />
          <Button
            kind="tertiary"
            size="sm"
            onClick={() => {
              setSwept(true);
              setPage(1);
            }}
          >
            Search nearby wells
          </Button>
        </div>
      </div>

      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Could not search"
          subtitle={error.message}
        />
      )}

      {/* Nothing of the result after a failure: the notification above has already said
          what happened, and an empty table under it reads as an answer of none. */}
      {swept && !error && (
        <>
          <Search
            labelText="Filter these results"
            placeholder="Filter by name or UWI"
            size="sm"
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setPage(1);
            }}
          />

          {/* Paged rather than truncated: a corridor across the basin can hold
              thousands of wells, and the tail is not less real than the head. The
              table draws the bar; the page it asks for is answered by the server. */}
          <WellPickerTable
            wells={candidates}
            stagedUwis={stagedUwis}
            sort={sort}
            onSort={(next) => {
              setSort(next);
              setPage(1);
            }}
            onStage={onStage}
            emptyMessage="No wells within that distance of the section."
            loading={isLoading}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={(next, nextSize) => {
              setPage(next);
              setPageSize(nextSize);
            }}
          />
        </>
      )}
    </>
  );
};
